import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { format, isValid, parse } from "date-fns";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  CheckCircle,
  File,
  Plus,
  Upload,
  X,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/_main/products/allocation/upload")({
  component: RouteComponent,
});

interface FileWithPreview extends File {
  preview?: string;
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
  expiryDate: z.string().optional(),
  useAI: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
  const [activeTab, setActiveTab] = useState("upload");
  const [file, setFile] = useState<FileWithPreview | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      expiryDate: "",
      useAI: false,
    },
  });

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files?.[0]) {
      const droppedFile = e.dataTransfer.files[0];
      handleFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Check if file is CSV or Excel
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (
      fileExtension !== "csv" &&
      fileExtension !== "xls" &&
      fileExtension !== "xlsx"
    ) {
      toast.error("Please upload only CSV or Excel files");
      return;
    }

    const fileWithPreview = Object.assign(file, {
      preview: URL.createObjectURL(file),
    });

    setFile(fileWithPreview);
    toast.success(`File "${file.name}" uploaded successfully`);
  };

  const onSubmit = (values: FormValues) => {
    // Submit logic here
    alert(JSON.stringify({ ...values, file }, null, 2));

    // If on upload tab, move to review tab
    if (activeTab === "upload") {
      setActiveTab("review");
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Function to format date as MM/DD/YYYY
  const formatDateString = (date: Date | undefined): string => {
    if (!date) {
      return "";
    }
    return format(date, "MM/dd/yyyy");
  };

  // Function to parse string to Date
  const parseDateString = (dateString: string): Date | undefined => {
    if (!dateString) {
      return undefined;
    }

    // Try to parse the date string
    const parsedDate = parse(dateString, "MM/dd/yyyy", new Date());

    // Return the date if valid, otherwise undefined
    return isValid(parsedDate) ? parsedDate : undefined;
  };

  // Sync date picker with form field
  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      form.setValue("expiryDate", formatDateString(date));
    }
  };

  // Function to apply input mask for MM/DD/YYYY format
  const applyDateMask = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Apply mask based on the number of digits entered
    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length <= 4) {
      return `${digits.substring(0, 2)}/${digits.substring(2)}`;
    }

    return `${digits.substring(0, 2)}/${digits.substring(2, 4)}/${digits.substring(4, 8)}`;
  };

  // Handle manual input with mask
  const handleManualDateInput = (value: string) => {
    // Apply mask to the input value
    const maskedValue = applyDateMask(value);
    form.setValue("expiryDate", maskedValue);

    // Only update the selectedDate if we have a complete date
    if (maskedValue.length === 10) {
      // MM/DD/YYYY is 10 characters
      const parsedDate = parseDateString(maskedValue);
      setSelectedDate(parsedDate);
    }
  };

  // Function to remove the uploaded file
  const removeFile = () => {
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    setFile(null);
  };

  return (
    <div className="flex flex-col gap-6 p-8">
      <Button
        onClick={handleGoBack}
        variant="ghost"
        className="flex w-fit items-center gap-2 px-0"
      >
        <ArrowLeft size={16} />
        Go back
      </Button>

      <h1 className="font-semibold text-2xl">
        Upload your product data and create a shareable sheet for your customers
      </h1>

      <div className="mt-4">
        <Tabs
          defaultValue="upload"
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="mb-6 flex w-fit bg-transparent">
            <TabsTrigger
              value="upload"
              className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
            >
              Upload & Configure
            </TabsTrigger>
            <TabsTrigger
              value="review"
              className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
            >
              Review
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div>
                  <h2 className="mb-1 font-semibold text-lg">
                    Sheet Information
                  </h2>
                  <p className="mb-6 text-gray-600 text-sm">
                    Provide basic information about your product sheet
                  </p>

                  <div className="flex flex-col gap-8 md:flex-row">
                    <div className="flex-1 space-y-4">
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="xyz"
                                className="mt-1"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <textarea
                                placeholder="Enter a description..."
                                className="mt-1 flex w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                rows={4}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="expiryDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Expiry Date ( Optional )</FormLabel>
                            <Popover>
                              <div className="relative">
                                <FormControl>
                                  <div className="relative flex items-center">
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        type="button"
                                        className="absolute top-0 left-0 z-10 h-full rounded-l-md px-3 text-gray-500 transition-colors hover:bg-transparent hover:text-gray-900"
                                      >
                                        <CalendarIcon className="h-4 w-4" />
                                      </Button>
                                    </PopoverTrigger>
                                    <Input
                                      placeholder="MM/DD/YYYY"
                                      className="pl-11"
                                      {...field}
                                      value={field.value || ""}
                                      onChange={(e) =>
                                        handleManualDateInput(e.target.value)
                                      }
                                    />
                                  </div>
                                </FormControl>
                              </div>
                              <PopoverContent
                                className="w-auto p-0"
                                align="start"
                              >
                                <Calendar
                                  mode="single"
                                  selected={selectedDate}
                                  onSelect={handleDateChange}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormDescription className="mt-1 text-gray-500 text-xs">
                              If set, the sheet will automatically expire on
                              this date
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="useAI"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-2 space-y-0">
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                className="mt-1"
                              />
                            </FormControl>
                            <div>
                              <FormLabel className="font-normal">
                                Use AI Recommended Prices
                              </FormLabel>
                              <FormDescription className="text-gray-500 text-sm">
                                Let AI optimize your product prices based on
                                market data
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex-1 space-y-6">
                      <div
                        className={cn(
                          "flex h-[250px] cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed transition-colors",
                          isDragging
                            ? "border-primary bg-primary/5"
                            : "border-gray-300 hover:border-gray-400",
                          file ? "bg-gray-50" : "",
                        )}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          accept=".csv,.xls,.xlsx"
                          onChange={handleFileChange}
                          className="hidden"
                          id="fileUpload"
                        />
                        {file ? (
                          <div className="flex h-full w-full flex-col items-center justify-center p-4">
                            <div className="mb-2 flex items-center justify-center rounded-full bg-green-100 p-3">
                              <CheckCircle className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="mt-2 flex w-full items-center justify-center gap-2">
                              <File className="h-5 w-5 text-gray-500" />
                              <span className="max-w-[200px] truncate font-medium">
                                {file.name}
                              </span>
                            </div>
                            <p className="mt-1 text-gray-500 text-sm">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-4 flex items-center gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile();
                              }}
                            >
                              <X className="h-4 w-4" />
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <label
                            htmlFor="fileUpload"
                            className="flex h-full w-full cursor-pointer flex-col items-center justify-center"
                          >
                            <div className="mb-4 rounded-full bg-gray-100 p-4">
                              <Upload className="h-6 w-6 text-gray-500" />
                            </div>
                            <p className="font-medium">Click to upload</p>
                            <p className="text-gray-500 text-sm">
                              or drag and drop
                            </p>
                            <p className="mt-2 text-gray-400 text-xs">
                              CSV, Excel (.xls, .xlsx) (max. 5MB)
                            </p>
                          </label>
                        )}
                      </div>

                      <div className="rounded-md border border-yellow-100 bg-yellow-50 p-6">
                        <h3 className="mb-2 font-medium text-base">
                          Excel File Format
                        </h3>
                        <p className="mb-4 text-sm">
                          Your excel file should include the following columns:
                          Products Name, SKU, Quantity, Price
                        </p>
                        <Button variant="outline" className="text-sm" size="sm">
                          Download Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button type="submit">Next</Button>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="review">
            <div className="overflow-x-auto rounded-md border">
              {/* Header row */}
              <div className="flex flex-col justify-between gap-4 p-6 md:flex-row md:gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-2xl">Products</span>
                  <Badge className="bg-brand-yellowGreen text-brand-darkGreen">
                    4 Products
                  </Badge>
                </div>

                <div className="flex space-x-6">
                  <div className="flex items-center space-x-2">
                    <Switch id="ai-pricing" />
                    <Label htmlFor="ai-pricing">Use AI Pricing</Label>
                  </div>
                </div>
              </div>

              {/* Column headers */}
              <div
                className="grid items-center border-t border-b bg-muted font-medium text-sm"
                style={{
                  gridTemplateColumns:
                    "minmax(200px, 35%) minmax(120px, 20%) minmax(100px, 15%) minmax(100px, 15%) minmax(100px, 15%)",
                }}
              >
                <div className="px-4 py-3">Name</div>
                <div className="px-4 py-3">SKU</div>
                <div className="px-4 py-3 text-right">Quantity</div>
                <div className="px-4 py-3 text-right">Price ( BHD )</div>
                <div className="px-4 py-3 text-center">AI Price</div>
              </div>

              {/* Product rows */}
              <div
                className="grid items-center border-b hover:bg-muted/50"
                style={{
                  gridTemplateColumns:
                    "minmax(200px, 35%) minmax(120px, 20%) minmax(100px, 15%) minmax(100px, 15%) minmax(100px, 15%)",
                }}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="h-10 w-10 overflow-hidden rounded-md bg-gray-100">
                    <img
                      src="/organic-apples.jpg"
                      alt="Organic Apples"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://placehold.co/40x40";
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Organic Apples</div>
                    <div className="text-muted-foreground text-xs">kg</div>
                  </div>
                </div>
                <div className="px-4 py-3">FRUIT-001</div>
                <div className="px-4 py-3 text-right">500</div>
                <div className="px-4 py-3 text-right">2.99</div>
                <div className="flex justify-center px-4 py-3">
                  <Badge className="bg-green-100 text-green-800">2.5</Badge>
                </div>
              </div>

              <div
                className="grid items-center border-b hover:bg-muted/50"
                style={{
                  gridTemplateColumns:
                    "minmax(200px, 35%) minmax(120px, 20%) minmax(100px, 15%) minmax(100px, 15%) minmax(100px, 15%)",
                }}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="h-10 w-10 overflow-hidden rounded-md bg-gray-100">
                    <img
                      src="/fresh-carrots.jpg"
                      alt="Fresh Carrots"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://placehold.co/40x40";
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Fresh Carrots</div>
                    <div className="text-muted-foreground text-xs">kg</div>
                  </div>
                </div>
                <div className="px-4 py-3">VEG-002</div>
                <div className="px-4 py-3 text-right">500</div>
                <div className="px-4 py-3 text-right">2.99</div>
                <div className="flex justify-center px-4 py-3">
                  <Badge className="bg-green-100 text-green-800">2.5</Badge>
                </div>
              </div>

              <div
                className="grid items-center border-b hover:bg-muted/50"
                style={{
                  gridTemplateColumns:
                    "minmax(200px, 35%) minmax(120px, 20%) minmax(100px, 15%) minmax(100px, 15%) minmax(100px, 15%)",
                }}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="h-10 w-10 overflow-hidden rounded-md bg-gray-100">
                    <img
                      src="/whole-milk.jpg"
                      alt="Whole Milk"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://placehold.co/40x40";
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Whole Milk</div>
                    <div className="text-muted-foreground text-xs">kg</div>
                  </div>
                </div>
                <div className="px-4 py-3">DAIRY-003</div>
                <div className="px-4 py-3 text-right">500</div>
                <div className="px-4 py-3 text-right">2.99</div>
                <div className="flex justify-center px-4 py-3">
                  <Badge className="bg-green-100 text-green-800">2.5</Badge>
                </div>
              </div>

              <div
                className="grid items-center border-b hover:bg-muted/50"
                style={{
                  gridTemplateColumns:
                    "minmax(200px, 35%) minmax(120px, 20%) minmax(100px, 15%) minmax(100px, 15%) minmax(100px, 15%)",
                }}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="h-10 w-10 overflow-hidden rounded-md bg-gray-100">
                    <img
                      src="/free-range-eggs.jpg"
                      alt="Free Range Eggs"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "https://placehold.co/40x40";
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Free Range Eggs</div>
                    <div className="text-muted-foreground text-xs">kg</div>
                  </div>
                </div>
                <div className="px-4 py-3">DAIRY-004</div>
                <div className="px-4 py-3 text-right">500</div>
                <div className="px-4 py-3 text-right">2.99</div>
                <div className="flex justify-center px-4 py-3">
                  <Badge className="bg-green-100 text-green-800">2.5</Badge>
                </div>
              </div>

              <div className="px-4 py-3">
                <div className="">Total</div>
                <div className="font-bold">BHD 3,263.50</div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" className="gap-2">
                Cancel
              </Button>
              <Button className="gap-2">Generate Sheet</Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
